import type { CalcOption, versionInfo } from "../types/budget/budget";

const CALC_OPTIONS = new Set<CalcOption>(["A", "B", "C", "D"]);

export type PhaseCalcOptionService = {
  calcOption?: string;
  budgetValues?: { calcOption?: string };
  negotiationValue?: Array<{ calcOption?: string } | undefined>;
  closingValues?: { calcOption?: string };
};

export type PhaseCalcOptionStage = Pick<versionInfo, "version"> & {
  status?: string;
};

function asCalcOption(value: string | undefined, fallback: string): CalcOption {
  if (value && CALC_OPTIONS.has(value as CalcOption)) {
    return value as CalcOption;
  }
  if (fallback && CALC_OPTIONS.has(fallback as CalcOption)) {
    return fallback as CalcOption;
  }
  return "A";
}

/** Orçamento / negociação / fechamento isolam a forma de cobrança A–D. */
export function isVersionedBillingStage(stage?: PhaseCalcOptionStage): boolean {
  const status = (stage?.status || "").toUpperCase();
  return (
    status === "APPROVED" || status === "APROVED" || status === "NEGOTIATE"
  );
}

/**
 * Forma de cobrança vigente na etapa. `service.calcOption` é a identidade do
 * orçamento; override por fase vive em `budgetValues` / `negotiationValue` /
 * `closingValues`.
 */
export function resolveServiceCalcOption(
  service: PhaseCalcOptionService | undefined,
  stage?: PhaseCalcOptionStage
): CalcOption {
  const identity = service?.calcOption || "A";
  if (!service) return asCalcOption(undefined, identity);

  const version = stage?.version ?? 0;
  const status = (stage?.status || "").toUpperCase();

  if (status === "APPROVED" || status === "APROVED") {
    return asCalcOption(service.closingValues?.calcOption, identity);
  }

  if (status === "NEGOTIATE" && version > 0) {
    return asCalcOption(
      service.negotiationValue?.[version - 1]?.calcOption,
      identity
    );
  }

  return asCalcOption(service.budgetValues?.calcOption, identity);
}

/** Forma de cobrança ao agregar `budgetValues` ou `closingValues`. */
export function resolvePhaseCalcOption(
  service: PhaseCalcOptionService | undefined,
  key: "budgetValues" | "closingValues"
): CalcOption {
  const identity = service?.calcOption || "A";
  if (!service) return asCalcOption(undefined, identity);
  return asCalcOption(service[key]?.calcOption, identity);
}
